// src/store/sessions/slice.ts
import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import {
  fetchSessions,
  sendRemoveRequest,
  confirmRemove,
  resendPinCode,
} from './thunks';
import { AccountSession, SessionStep } from './types';
import { UUID } from '@/types';
import { ValidationErrorType } from '@/types/ValidationError.ts';

type TargetIds = UUID[];

interface SessionsState {
  current: AccountSession | null;
  others: AccountSession[];
  loading: boolean;
  error?: string;

  step: SessionStep;
  targetIds: TargetIds | null;
  /** validation message for the PIN field (null = no error) */
  pinError?: ValidationErrorType;
  resendTime: number | null;
}

const initialState: SessionsState = {
  current: null,
  others: [],
  loading: false,
  step: SessionStep.Init,
  targetIds: null,
  resendTime: null,
};

const slice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    askEndOne(state, { payload: id }: PayloadAction<UUID>) {
      state.targetIds = [id];
      state.step = SessionStep.ConfirmOne;
    },
    askEndAll(state) {
      /* build the full list right now */
      const allIds = [
        ...(state.current ? [state.current.id] : []),
        ...state.others.map((s) => s.id),
      ];
      state.targetIds = allIds;
      state.step = SessionStep.ConfirmAll;
    },
    cancel(state) {
      state.step = SessionStep.List;
      state.targetIds = null;
    },
    setStep(state, { payload }: PayloadAction<SessionStep>) {
      state.step = payload;
      state.error = undefined;
      state.pinError = undefined;
    },
    resetErrors(state) {
      state.error = undefined;
      state.pinError = undefined;
    },
  },

  extraReducers: (b) => {
    b.addCase(fetchSessions.fulfilled, (s, { payload }) => {
      s.current = payload.find((p) => p.current) ?? null;
      s.others = payload.filter((p) => !p.current);
      s.step = SessionStep.List;
    });
    b.addCase(fetchSessions.rejected, (s, { error }) => {
      s.error = error.message;
    });

    /*  /remove  -> show PIN modal */
    b.addCase(sendRemoveRequest.fulfilled, (s, { payload }) => {
      s.step = SessionStep.Pin;
      s.resendTime = payload ?? null;
    });
    b.addCase(sendRemoveRequest.rejected, (s, { error }) => {
      s.error = error.message;
    });

    // resend pin
    b.addCase(resendPinCode.fulfilled, (s, { payload }) => {
      s.resendTime = payload ?? null; // ← update on every resend
    });
    b.addCase(resendPinCode.rejected, (s, { payload }) => {
      if (payload) s.error = payload; // generic toast-level message
    });

    /*  /confirm/remove  */
    b.addCase(confirmRemove.fulfilled, (s) => {
      s.step = SessionStep.Success;
      s.targetIds = null;
      s.pinError = undefined;
    });
    b.addCase(confirmRemove.rejected, (s, { payload, error }) => {
      if (payload?.pinError) {
        s.pinError = payload.pinError;
        s.step = SessionStep.Pin;
      } else {
        s.error = error.message; // network / 5xx fallback
      }
    });

    b.addMatcher(
      (a): a is UnknownAction =>
        a.type.startsWith('sessions/') && a.type.endsWith('/pending'),
      (s) => {
        s.loading = true;
        s.error = undefined; // optional
        s.pinError = undefined; // optional
      }
    );

    b.addMatcher(
      (a): a is UnknownAction =>
        a.type.startsWith('sessions/') &&
        (a.type.endsWith('/fulfilled') || a.type.endsWith('/rejected')),
      (s) => {
        s.loading = false;
      }
    );
  },
});

export const { askEndOne, resetErrors, askEndAll, cancel, setStep } =
  slice.actions;

export default slice;
